import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  onPointerDownOutside?: (e: PointerEvent) => void;
  // If provided, the dialog will render within this container instead of the body
  container?: Element | null;
}

export function Dialog({
  isOpen,
  onClose,
  children,
  className = "",
  onPointerDownOutside,
  container,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.addEventListener("keydown", handleEscape);
      // Only lock body scroll if rendering to body
      if (!container) {
        document.body.style.overflow = "hidden";
      }

      return () => {
        document.removeEventListener("keydown", handleEscape);
        if (!container) {
          document.body.style.overflow = originalOverflow;
        }
      };
    }
  }, [isOpen, onClose]);

  const handleBackdropPointerDown = (event: React.PointerEvent) => {
    if (event.target === event.currentTarget) {
      // Don't dismiss dialog when clicking inside toast
      if (
        event.target instanceof Element &&
        event.target.closest("[data-sonner-toast]")
      ) {
        return;
      }
      onClose();
      onPointerDownOutside?.(event.nativeEvent);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={cn(
        container ? "absolute" : "fixed",
        "inset-0 z-50 flex items-center justify-center bg-black/50",
      )}
      onPointerDown={handleBackdropPointerDown}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        className={`relative bg-white ${className}`}
      >
        {children}
      </div>
    </div>,
    container || document.body,
  );
}
